// HeroBanner.js

import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { FiArrowRight, FiCheckCircle, FiPhone } from "react-icons/fi";

const zoomEffect = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.12);
  }
`;

const fadeUp = keyframes`
  from {
    opacity:0;
    transform:translateY(35px);
  }
  to {
    opacity:1;
    transform:translateY(0);
  }
`;

const floatAnimation = keyframes`
  0%,100%{
    transform:translateY(0);
  }

  50%{
    transform:translateY(-8px);
  }
`;


const HeroWrapper = styled.section`

position:relative;

width:100%;

min-height:100vh;

display:flex;

align-items:center;

justify-content:center;

overflow:hidden;

background:${({ theme }) => theme.colors.bgDark};

padding-top:80px;

color:white;


`;



const BackgroundImage = styled.div`

position:absolute;

inset:0;


background-image:url(${({ image }) => image});

background-size:cover;

background-position:center;


opacity:${({ active }) => active ? 1 : 0};


transition:opacity 1.5s ease-in-out;


&::after{

content:"";

position:absolute;

inset:0;


background:
linear-gradient(
180deg,
rgba(15,23,42,.55),
rgba(15,23,42,.88)
);

}


${({ active }) =>
    active &&
    css`
    animation: ${zoomEffect} 8s ease forwards;
  `}


`;



const Content = styled.div`

position:relative;

z-index:5;


max-width:950px;

width:100%;


padding:40px 24px;


text-align:center;


animation:${fadeUp} .8s ease;


`;



const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: 50px;
  background: rgba(15, 139, 66, 0.18);
  border: 1px solid rgba(52, 211, 153, 0.45);
  color: #86efac;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 28px;
  backdrop-filter: blur(10px);

  /* SVG Icon Fixes for Mobile & Responsiveness */
  svg {
    font-size: 1rem;       /* Keeps icon proportionate to font-size */
    flex-shrink: 0;        /* Prevents icon from squishing when text wraps */
    display: block;        /* Removes inline extra spacing below SVG */
  }

  @media (max-width: 480px) {
    padding: 6px 14px;     /* Comfortable padding for small screens */
    font-size: 0.725rem;   /* Slight font reduction for compact mobile screens */
    gap: 6px;

    svg {
      font-size: 0.9rem;   /* Scales icon down proportionally with text */
    }
  }
`;



const Heading = styled.h1`

font-size:clamp(2.3rem,5vw,4rem);


font-weight:800;


line-height:1.12;


letter-spacing:-1.5px;


color:white;


margin-bottom:22px;


span{

color:${({ theme }) => theme.colors.primary};

}


`;



const Description = styled.p`

max-width:760px;

margin:0 auto 35px;


font-size:clamp(1rem,2vw,1.2rem);


line-height:1.7;


color:rgba(255,255,255,.82);


`;



const Buttons = styled.div`

display:flex;

justify-content:center;

align-items:center;

gap:18px;


@media(max-width:${({ theme }) => theme.breakpoints.mobile}){

flex-direction:column;

width:100%;

}


`;



const PrimaryButton = styled.a`

display:flex;

align-items:center;

justify-content:center;

gap:10px;


padding:15px 32px;


border-radius:50px;


background:

linear-gradient(
135deg,
${({ theme }) => theme.colors.primary},
${({ theme }) => theme.colors.secondary}
);


color:white;


font-weight:700;


text-decoration:none;


box-shadow:

0 15px 35px rgba(15,139,66,.35);


transition:.35s;


&:hover{

transform:translateY(-4px);

color:white;

}


@media(max-width:${({ theme }) => theme.breakpoints.mobile}){

width:100%;

}

`;



const SecondaryButton = styled.a`

display:flex;

align-items:center;

justify-content:center;

gap:10px;


padding:15px 32px;


border-radius:50px;


background:rgba(255,255,255,.12);


border:1px solid rgba(255,255,255,.25);


color:white;


font-weight:700;


text-decoration:none;


backdrop-filter:blur(10px);


transition:.35s;


&:hover{

background:rgba(255,255,255,.2);

transform:translateY(-4px);

color:white;

}


@media(max-width:${({ theme }) => theme.breakpoints.mobile}){

width:100%;

}

`;



const TrustRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 35px;
  margin-top: 45px;
  color: white;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start; /* Aligns all items cleanly to the left edge */
    width: fit-content;      /* Shrinks container to fit content */
    margin-left: auto;       /* Centers the container horizontally */
    margin-right: auto;
    gap: 14px;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primaryLight || "#34d399"};
    font-size: 1.1rem;
  }
`;



const ScrollIndicator = styled.div`

position:absolute;

bottom:35px;


left:50%;

transform:translateX(-50%);


z-index:5;


display:flex;

flex-direction:column;

align-items:center;

gap:8px;


font-size:.75rem;


color:white;


animation: ${floatAnimation} 2s infinite;


`;



const Dots = styled.div`

position:absolute;

bottom:35px;

right:40px;


display:flex;

gap:10px;


z-index:6;


@media(max-width:${({ theme }) => theme.breakpoints.mobile}){

right:50%;

transform:translateX(50%);

bottom:25px;

}

`;



const Dot = styled.button`

width:${({ active }) => active ? "35px" : "10px"};

height:10px;


border-radius:20px;


border:none;


background:${({ active, theme }) =>

    active

      ? theme.colors.primary

      : "rgba(255,255,255,.4)"

  };


cursor:pointer;


transition:.3s;


`;



const slides = [

  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=85",

  "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1920&q=85",

  "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1920&q=85"

];



export default function HeroBanner() {


  const [currentSlide, setCurrentSlide] = useState(0);



  useEffect(() => {


    const timer = setInterval(() => {


      setCurrentSlide(prev =>

        (prev + 1) % slides.length

      );


    }, 6000);


    return () => clearInterval(timer);


  }, []);



  return (

    <HeroWrapper id="home">


      {

        slides.map((image, index) => (

          <BackgroundImage

            key={image}

            image={image}

            active={index === currentSlide}

          />

        ))

      }



      <Content>


        <Badge>

          <FiCheckCircle />

          Certified Contracting Excellence • UAE

        </Badge>



        <Heading>

          Premium <span>Plastering</span> &

          <br />

          Tiling Solutions

          <br />

          Across Sharjah & UAE

        </Heading>



        <Description>

          Delivering exceptional wall finishes, luxury tile installation,
          and precision craftsmanship for residential and commercial spaces.

        </Description>



        <Buttons>


          <PrimaryButton href="#contact">

            <FiPhone />

            Request Free Quote

          </PrimaryButton>


          <SecondaryButton href="#projects">

            Explore Projects

            <FiArrowRight />

          </SecondaryButton>


        </Buttons>



        <TrustRow>


          <TrustItem>

            <FiCheckCircle />

            10+ Years Experience

          </TrustItem>


          <TrustItem>

            <FiCheckCircle />

            500+ Completed Projects

          </TrustItem>


          <TrustItem>

            <FiCheckCircle />

            UAE Quality Standards

          </TrustItem>


        </TrustRow>



      </Content>




      {/* <ScrollIndicator>

↓
<br/>

Scroll

</ScrollIndicator> */}



      <Dots>

        {

          slides.map((_, index) => (

            <Dot

              key={index}

              active={index === currentSlide}

              onClick={() => setCurrentSlide(index)}

            />

          ))

        }

      </Dots>



    </HeroWrapper>

  );

}